import React, { memo } from 'react';
import { Grid, Link, Theme, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { routes } from 'utils/routing';
import { formatDuration, formatTimeDuration } from 'utils/format';
import { isSessionEndTimeHidden } from 'utils/transform';
import Image from 'components/common/Image';

interface IProps {
  eventImage?: string;
  eventTitle: string;
  startAt: string;
  endAt: string;
  timeZone: string;
  sessions: any[];
  organization: {
    title: string;
    id: string;
  };
  hosts?: number;
}

const useStyles = makeStyles()((theme) => ({
  textDate: {
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  textBy: {
    fontWeight: 500,
  },
  collapsedContainer: {
    padding: theme.spacing(1, 2),
  },
  linksWeight: {
    fontWeight: 500,
    fontSize: 13,
  },
}));

const EventDetails: React.FC<React.PropsWithChildren<IProps>> = ({
  eventImage,
  eventTitle,
  sessions,
  startAt,
  endAt,
  timeZone,
  organization,
  hosts,
}) => {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const hideEndTime = isSessionEndTimeHidden(sessions, endAt);
  const time = startAt && endAt && formatTimeDuration(startAt, hideEndTime ? null : endAt, timeZone);
  const duration = startAt && endAt && formatDuration(startAt, endAt, timeZone);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Grid container spacing={3} alignItems="center">
      {!isMobile && (
        <Grid item xs={3.7}>
          <Image rounded src={eventImage} alt="image" variant="rectangle" />
        </Grid>
      )}
      <Grid item xs style={{ paddingTop: isMobile ? 21 : 21 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography variant="h6">{eventTitle}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary" className={classes.textDate}>
              {duration}{' '}
              {Array.isArray(sessions) && sessions.length > 1 ? (
                t('common:general.multipleTimes')
              ) : (
                <>
                  {time?.split('|')[0]} <span>{time?.split('|')[1]}</span>
                </>
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ ...(isMobile && { marginTop: '8px' }) }}>
            <Grid container spacing={0.5} alignItems="center">
              <Grid item>
                <Typography variant="body2" color="textSecondary" className={classes.textBy}>
                  {t<string>('common:general.by')}
                </Typography>
              </Grid>
              <Grid item>
                <Link
                  href={routes.organizationProfile(organization.id).link}
                  underline="none"
                  target="_blank"
                  className={classes.linksWeight}
                >
                  {organization.title}
                </Link>
              </Grid>
              {hosts > 0 && (
                <Grid item>
                  <Link underline="none" href="#" className={classes.linksWeight}>
                    {t<string>('common:general.plusOther', { count: hosts })}
                  </Link>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo(EventDetails);
